import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import {
  MARK, CATEGORIAS_DE_INSUMO, ROTULO_DA_CATEGORIA, ETAPAS_DE_CUSTO, ROTULO_DA_ETAPA_DE_CUSTO, UNIDADES,
} from '../../../constants';
import {
  listInsumos, insertInsumo, updateInsumo, deleteInsumo, importarSugeridos, sugeridosQueFaltam,
  listMovimentos, lancarMovimento, excluirMovimento,
} from '../../../data/insumos';
import { confirmarRemocao } from '../../../utils/confirmarRemocao';

const campo = 'w-full px-2 py-1.5 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';
const num = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const reais = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoje = () => new Date().toISOString().slice(0, 10);

const INSUMO_VAZIO = { nome: '', categoria: 'descartavel', unidade: 'un', etapa: 'geral', preco: '', controla_estoque: false };

/**
 * Catálogo de insumos e, para quem quiser, o estoque deles.
 *
 * É daqui que os custos do serviço puxam unidade e preço. Preço é opcional —
 * cotação muda, e um número velho aqui viraria cobrança errada sem ninguém
 * perceber. Estoque também: só o insumo marcado ganha saldo.
 *
 * A saída é automática: cada linha de custo de um serviço com um insumo que
 * controla estoque baixa o saldo. Aqui se lança só o que entra (compra) e o
 * ajuste de inventário.
 */
const EstoqueDeInsumos = ({ laboratoryId, podeMexer }) => {
  const [insumos, setInsumos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [etapa, setEtapa] = useState('');
  const [novo, setNovo] = useState(null);
  const [aberto, setAberto] = useState(null);
  // Os sugeridos marcados para importar; `null` = painel fechado. Nasce com
  // todos marcados: desmarcar o que não se usa é mais rápido que marcar o resto.
  const [importando, setImportando] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setInsumos(await listInsumos(laboratoryId) || []);
      setErro('');
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [laboratoryId]);

  useEffect(() => { carregar(); }, [carregar]);

  const faltam = useMemo(() => sugeridosQueFaltam(insumos), [insumos]);

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return insumos.filter(i => (!etapa || i.etapa === etapa)
      && (!q || i.nome.toLowerCase().includes(q) || (ROTULO_DA_CATEGORIA[i.categoria] || '').toLowerCase().includes(q)));
  }, [insumos, busca, etapa]);

  const acao = async (fn) => {
    try {
      setErro('');
      await fn();
      await carregar();
    } catch (e) {
      // O índice único fala em inglês; quem lê é a equipe.
      setErro(/duplicate|unique/i.test(e.message) ? 'Já existe um insumo com esse nome.' : e.message);
    }
  };

  const salvarNovo = () => acao(async () => {
    await insertInsumo({
      ...novo,
      nome: novo.nome.trim(),
      preco: novo.preco === '' ? null : Number(novo.preco),
      laboratory_id: laboratoryId,
    });
    setNovo(null);
  });

  if (carregando) return <p className="text-sm text-slate-500 px-1">Carregando insumos…</p>;

  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[12rem]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${campo} pl-8`} placeholder="Buscar insumo" value={busca}
            onChange={(e) => setBusca(e.target.value)} />
        </div>
        <select className={`${campo} w-auto`} value={etapa} onChange={(e) => setEtapa(e.target.value)}
          aria-label="Etapa">
          <option value="">Todas as etapas</option>
          {ETAPAS_DE_CUSTO.map(e => <option key={e.id} value={e.id}>{e.rotulo}</option>)}
        </select>
        {podeMexer && faltam.length > 0 && !importando && (
          <button onClick={() => setImportando(new Set(faltam.map(s => s.nome)))}
            className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50">
            Importar lista sugerida ({faltam.length})
          </button>
        )}
        {podeMexer && !novo && (
          <button onClick={() => setNovo(INSUMO_VAZIO)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700">
            <Plus size={14} /> Novo insumo
          </button>
        )}
      </div>

      {importando && (
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
            <p className="text-sm font-medium text-slate-900 mr-auto">Desmarque o que vocês não usam</p>
            <button onClick={() => setImportando(new Set(faltam.map(s => s.nome)))} className="text-xs text-blue-600 hover:underline">Marcar todos</button>
            <button onClick={() => setImportando(new Set())} className="text-xs text-blue-600 hover:underline">Desmarcar todos</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {ETAPAS_DE_CUSTO.map(e => {
              const daEtapa = faltam.filter(s => s.etapa === e.id);
              if (daEtapa.length === 0) return null;
              return (
                <fieldset key={e.id}>
                  <legend className="text-xs font-semibold text-slate-600 mb-1">{e.rotulo}</legend>
                  {daEtapa.map(s => (
                    <label key={s.nome} className="flex items-center gap-2 py-0.5 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" className="h-4 w-4" checked={importando.has(s.nome)}
                        onChange={(ev) => setImportando(atual => {
                          const prox = new Set(atual);
                          if (ev.target.checked) prox.add(s.nome); else prox.delete(s.nome);
                          return prox;
                        })} />
                      {s.nome}
                    </label>
                  ))}
                </fieldset>
              );
            })}
          </div>
          <div className="flex gap-2 mt-3">
            <button disabled={importando.size === 0}
              onClick={() => acao(async () => {
                await importarSugeridos(laboratoryId, insumos, [...importando]);
                setImportando(null);
              })}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              Importar {importando.size}
            </button>
            <button onClick={() => setImportando(null)}
              className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-md">Cancelar</button>
          </div>
        </div>
      )}

      {erro && <p className="mx-4 mt-3 text-sm text-slate-700 border border-slate-300 rounded px-3 py-2">{MARK.WARN} {erro}</p>}

      {novo && (
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <FormularioDoInsumo valor={novo} onChange={setNovo} />
          <div className="flex gap-2 mt-3">
            <button onClick={salvarNovo} disabled={!novo.nome.trim()}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              Cadastrar
            </button>
            <button onClick={() => setNovo(null)}
              className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-md">Cancelar</button>
          </div>
        </div>
      )}

      {insumos.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500">
          Nenhum insumo cadastrado.
          {podeMexer && ' Comece importando a lista sugerida — meios, seringas, agulhas, hormônios — e ajuste o que não usar.'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {visiveis.map(i => (
            <LinhaDoInsumo key={i.id} insumo={i} podeMexer={podeMexer}
              aberto={aberto === i.id} onAlternar={() => setAberto(a => (a === i.id ? null : i.id))}
              acao={acao} />
          ))}
          {visiveis.length === 0 && <li className="px-4 py-6 text-sm text-slate-500 text-center">Nada com esse filtro.</li>}
        </ul>
      )}
    </div>
  );
};

const FormularioDoInsumo = ({ valor, onChange }) => {
  const m = (campoNome, v) => onChange({ ...valor, [campoNome]: v });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
      <div className="col-span-2">
        <label className="block text-xs text-slate-600 mb-1">Nome</label>
        <input className={campo} value={valor.nome} onChange={(e) => m('nome', e.target.value)} placeholder="Ex.: Seringa 10 mL" />
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Categoria</label>
        <select className={campo} value={valor.categoria} onChange={(e) => m('categoria', e.target.value)}>
          {CATEGORIAS_DE_INSUMO.map(c => <option key={c.id} value={c.id}>{c.rotulo}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Etapa</label>
        <select className={campo} value={valor.etapa} onChange={(e) => m('etapa', e.target.value)}>
          {ETAPAS_DE_CUSTO.map(e => <option key={e.id} value={e.id}>{e.rotulo}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Unidade</label>
        <select className={campo} value={valor.unidade} onChange={(e) => m('unidade', e.target.value)}>
          {(UNIDADES.includes(valor.unidade) ? UNIDADES : [valor.unidade, ...UNIDADES]).map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-600 mb-1">Preço (opcional)</label>
        <input type="number" min="0" step="0.01" className={campo} placeholder="—"
          value={valor.preco ?? ''} onChange={(e) => m('preco', e.target.value)} />
      </div>
      <label className="col-span-2 sm:col-span-6 flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4" checked={!!valor.controla_estoque}
          onChange={(e) => m('controla_estoque', e.target.checked)} />
        Controlar estoque (dá baixa quando o insumo é lançado no custo de um serviço)
      </label>
    </div>
  );
};

const LinhaDoInsumo = ({ insumo, podeMexer, aberto, onAlternar, acao }) => {
  const [edicao, setEdicao] = useState(null);
  const [movs, setMovs] = useState(null);
  const [mov, setMov] = useState({ tipo: 'entrada', quantidade: '', data: hoje(), custo_unitario: '', observacao: '' });

  useEffect(() => {
    if (!aberto) return;
    setEdicao({ ...insumo, preco: insumo.preco ?? '' });
    listMovimentos(insumo.id).then(setMovs).catch(() => setMovs([]));
  }, [aberto, insumo]);

  const baixo = insumo.controla_estoque && insumo.estoque_minimo !== null
    && Number(insumo.saldo) <= Number(insumo.estoque_minimo);

  const excluir = () => {
    const aviso = insumo.controla_estoque
      ? `Excluir "${insumo.nome}"? Os custos já lançados nos serviços continuam, mas o histórico de estoque dele é apagado.`
      : `Excluir "${insumo.nome}" do catálogo? Os custos já lançados nos serviços continuam.`;
    if (confirmarRemocao(aviso)) acao(() => deleteInsumo(insumo.id));
  };

  const salvar = () => acao(() => updateInsumo(insumo.id, {
    nome: edicao.nome.trim(), categoria: edicao.categoria, etapa: edicao.etapa, unidade: edicao.unidade,
    preco: edicao.preco === '' ? null : Number(edicao.preco), controla_estoque: !!edicao.controla_estoque,
  }));

  const lancar = () => acao(async () => {
    const q = Number(mov.quantidade);
    await lancarMovimento({
      laboratory_id: insumo.laboratory_id, insumo_id: insumo.id, tipo: mov.tipo,
      quantidade: q, data: mov.data || hoje(),
      custo_unitario: mov.custo_unitario === '' ? null : Number(mov.custo_unitario),
      observacao: mov.observacao.trim() || null,
    });
    setMov(m => ({ ...m, quantidade: '', custo_unitario: '', observacao: '' }));
  });

  return (
    <li className={insumo.ativo ? '' : 'opacity-60'}>
      <div className="flex items-center hover:bg-slate-50">
      <button onClick={onAlternar} className="flex-1 min-w-0 pl-4 pr-2 py-2.5 flex items-center gap-3 text-left">
        {aberto ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-slate-900 truncate">
            {insumo.nome}{!insumo.ativo && <span className="font-normal text-slate-500"> · inativo</span>}
          </span>
          <span className="block text-xs text-slate-500 truncate">
            {ROTULO_DA_CATEGORIA[insumo.categoria]} · {ROTULO_DA_ETAPA_DE_CUSTO[insumo.etapa]}
            {insumo.preco !== null && ` · R$ ${reais(insumo.preco)}/${insumo.unidade}`}
          </span>
        </span>
        <span className="text-sm tabular-nums text-slate-700 whitespace-nowrap">
          {insumo.controla_estoque ? <>{baixo && `${MARK.WARN} `}{num(insumo.saldo)} {insumo.unidade}</> : <span className="text-slate-400">sem estoque</span>}
        </span>
      </button>
      {/* Excluir à mão, sem abrir: é como se limpa o catálogo do que o
          laboratório não trabalha. */}
      {podeMexer && (
        <button onClick={excluir} title="Excluir do catálogo" aria-label={`Excluir ${insumo.nome}`}
          className="p-2 mr-2 text-slate-400 hover:text-red-600 transition-colors">
          <Trash2 size={16} />
        </button>
      )}
      </div>

      {aberto && edicao && (
        <div className="px-4 pb-4 pt-1 space-y-4 bg-slate-50 border-t border-slate-200">
          {podeMexer && (
            <div>
              <FormularioDoInsumo valor={edicao} onChange={setEdicao} />
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={salvar} disabled={!edicao.nome.trim()}
                  className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">Salvar</button>
                <button onClick={() => acao(() => updateInsumo(insumo.id, { ativo: !insumo.ativo }))}
                  className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-md"
                  title="Inativo some da lista do custo, mas continua nos serviços já lançados">
                  {insumo.ativo ? 'Desativar' : 'Reativar'}
                </button>
                <button onClick={excluir} className="px-3 py-1.5 text-sm text-slate-500 hover:text-red-600">Excluir</button>
              </div>
            </div>
          )}

          {insumo.controla_estoque && (
            <div>
              <h5 className="text-xs font-semibold text-slate-700 mb-2">Estoque</h5>
              {podeMexer && (
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end mb-3">
                  <select className={campo} value={mov.tipo} onChange={(e) => setMov({ ...mov, tipo: e.target.value })} aria-label="Tipo">
                    <option value="entrada">Entrada (compra)</option>
                    <option value="ajuste">Ajuste (inventário)</option>
                  </select>
                  <input type="number" step="any" className={campo} placeholder={mov.tipo === 'ajuste' ? '+/− qtd' : 'Qtd'}
                    value={mov.quantidade} onChange={(e) => setMov({ ...mov, quantidade: e.target.value })} aria-label="Quantidade" />
                  <input type="date" className={campo} value={mov.data} onChange={(e) => setMov({ ...mov, data: e.target.value })} aria-label="Data" />
                  <input type="number" min="0" step="0.01" className={campo} placeholder="R$ unit. (opcional)"
                    value={mov.custo_unitario} onChange={(e) => setMov({ ...mov, custo_unitario: e.target.value })} aria-label="Custo unitário" />
                  <input className={campo} placeholder="Observação" value={mov.observacao}
                    onChange={(e) => setMov({ ...mov, observacao: e.target.value })} aria-label="Observação" />
                  <button onClick={lancar}
                    disabled={!Number(mov.quantidade) || (mov.tipo === 'entrada' && Number(mov.quantidade) < 0)}
                    className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">Lançar</button>
                </div>
              )}
              {movs === null ? <p className="text-xs text-slate-500">Carregando…</p> : movs.length === 0 ? (
                <p className="text-xs text-slate-500">Nenhum movimento ainda.</p>
              ) : (
                <ul className="text-xs divide-y divide-slate-200 border border-slate-200 rounded bg-white">
                  {movs.map(m => (
                    <li key={m.id} className="px-3 py-1.5 flex items-center gap-3">
                      <span className="tabular-nums text-slate-500 w-20">{m.data.split('-').reverse().join('/')}</span>
                      <span className="flex-1 min-w-0 truncate text-slate-700">
                        {m.tipo === 'entrada' ? 'Entrada' : m.tipo === 'ajuste' ? 'Ajuste' : 'Saída no serviço'}
                        {m.observacao && ` · ${m.observacao}`}
                      </span>
                      <span className="tabular-nums font-medium text-slate-900">
                        {m.tipo === 'saida' ? '−' : (m.tipo === 'ajuste' && m.quantidade < 0 ? '' : '+')}{num(m.quantidade)}
                      </span>
                      {podeMexer && m.origem === 'manual' ? (
                        <button onClick={() => acao(async () => { await excluirMovimento(m.id); setMovs(await listMovimentos(insumo.id)); })}
                          className="text-slate-400 hover:text-red-600" title="Excluir lançamento"><Trash2 size={13} /></button>
                      ) : <span className="w-[13px]" />}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default EstoqueDeInsumos;
